import styled, { css } from "styled-components";

// shared by Board (players) and Chat: the floating sticker panel
export const floatingPanel = css`
  background: var(--card);
  border: var(--border-w) solid var(--ink);
  border-radius: var(--radius-xl);
  box-shadow:
    0 var(--lip) 0 var(--ink),
    var(--shadow-float);
`;

export const BoardContainer = styled.div<{
  $open?: boolean;
  $finished?: boolean;
}>`
  position: fixed;
  z-index: ${({ $open }) => ($open ? 20 : 10)};
  top: ${({ $finished }) => ($finished ? "var(--space-4)" : "12px")};
  left: ${({ $finished }) => ($finished ? "50%" : "12px")};
  translate: ${({ $finished }) => ($finished ? "-50% 0" : "0 0")};
  bottom: ${({ $finished }) => ($finished ? "var(--space-4)" : "unset")};
  width: ${({ $open, $finished }) =>
    $finished
      ? "min(28rem, calc(100vw - var(--space-8)))"
      : $open
        ? "280px"
        : "auto"};
  max-width: calc(100vw - 24px);
  padding: ${({ $open }) => ($open ? "var(--space-4)" : "0")};
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  transition:
    width var(--dur-base) var(--ease-bounce),
    padding var(--dur-base) var(--ease-bounce);

  ${({ $open }) =>
    $open &&
    css`
      ${floatingPanel}
      animation: pop-in var(--dur-slow) var(--ease-bounce) both;
      transform-origin: top left;
    `}

  ${({ $finished }) =>
    $finished &&
    css`
      transform-origin: top center;
      padding: var(--space-6) var(--space-5);
    `}

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: ${({ $open }) => ($open ? "70vh" : "0px")};
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
`;

export const BoardHeader = styled.div`
  display: flex;
  gap: var(--space-2);
  align-items: center;
  justify-content: space-between;

  h2 {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-lg);
    font-weight: 600;
  }
`;

export const FinalHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  text-align: center;

  > span {
    display: grid;
    place-items: center;
    width: 64px;
    height: 64px;
    border-radius: var(--radius-lg);
    background: var(--gold);
    color: var(--ink);
    border: var(--border-w) solid var(--ink);
    box-shadow: 0 var(--lip) 0 var(--gold-shade);
    rotate: -6deg;
    animation: tada 900ms var(--ease-out) both;
  }

  h2 {
    font-size: var(--text-xl);
    font-weight: 700;
  }
`;

export const PlayerRow = styled.li<{
  $you?: boolean;
  $offline?: boolean;
  $rank?: number;
  $index: number;
}>`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  border: var(--border-w) solid
    ${({ $you }) => ($you ? "var(--teal)" : "var(--line)")};
  background: ${({ $you, $rank }) =>
    $rank === 0
      ? "var(--gold-tint)"
      : $you
        ? "var(--teal-tint)"
        : "var(--card)"};
  opacity: ${({ $offline }) => ($offline ? 0.55 : 1)};
  animation: pop-in var(--dur-slow) var(--ease-bounce) both;
  animation-delay: ${({ $index }) => $index * 50}ms;

  ${({ $rank }) =>
    $rank === 0 &&
    css`
      border-color: var(--gold-shade);
      box-shadow: 0 3px 0 var(--gold-shade);
      padding: var(--space-3);
    `}
`;

export const PlayerName = styled.span`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
  font-weight: 700;
  color: var(--ink);

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const Points = styled.span<{ $big?: boolean }>`
  font-family: var(--font-display);
  font-weight: 700;
  font-size: ${({ $big }) => ($big ? "var(--text-xl)" : "var(--text-lg)")};
  font-variant-numeric: tabular-nums;
  color: var(--ink);
`;

export const AvatarSlot = styled.div<{ $waiting?: boolean }>`
  position: relative;
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  color: var(--ink-faint);

  .MuiCircularProgress-root {
    position: absolute;
    inset: -4px;
    width: 40px !important;
    height: 40px !important;
    color: var(--teal);
  }
`;

export const Medal = styled.span<{ $place: number }>`
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-full);
  font-family: var(--font-display);
  font-weight: 700;
  border: var(--border-w) solid var(--ink);
  color: var(--ink);
  background: ${({ $place }) =>
    ["var(--gold)", "var(--line-strong)", "var(--cherry)"][$place] ||
    "var(--card-sunken)"};
  box-shadow: 0 2px 0 var(--ink);
`;

export const ChartPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3);
  border-radius: var(--radius-md);
  border: var(--border-w) solid var(--line);
  background: var(--card-sunken);
  animation: pop-in var(--dur-slow) var(--ease-bounce) both;
  animation-delay: 240ms;

  h3 {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--text-base);
    font-weight: 700;
    color: var(--ink);
  }

  svg {
    max-height: 32vh;
    overflow: visible;
  }

  .grid {
    stroke: var(--line-strong);
    stroke-width: 1;
    stroke-dasharray: 4 4;
  }

  .axis {
    font-family: var(--font-body);
    font-size: 11px;
    font-weight: 600;
    fill: var(--ink-faint);
  }

  .trail {
    fill: none;
    stroke-width: 3;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: var(--len);
    stroke-dashoffset: var(--len);
    animation: draw-in var(--dur-slow) var(--ease-out) both;
  }

  .dot {
    stroke: var(--card);
    stroke-width: 2;
  }

  .name {
    font-family: var(--font-body);
    font-size: 11px;
    font-weight: 700;
    fill: var(--ink-soft);
  }
`;
